import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';
import ProductCatalog, { Pricing } from '../../components/ProductCatalog';
import { productNames } from '../../products';
import Seo from '../../components/Seo';
import { siteUrl, PURCHASES_ENABLED } from '../../Constants';
import './styles/Sale.scss';

export default function Sale() {
    const [pricing, setPricing] = useState<Pricing[]>([]);
    const [ownedProducts, setOwnedProducts] = useState<number[]>([]);
    const [params] = useSearchParams();
    const api = new ApiHelper();

    const loggedIn = localStorage.getItem("token") !== null;

    // Optional ?plan=<productId> from the landing page — highlight that card.
    const planRaw = params.get("plan");
    const highlightProduct = planRaw != null && Number.isInteger(Number(planRaw)) ? Number(planRaw) : undefined;

    useEffect(() => {
        fetchPricing();
        // Signed-in visitors see products they already own (or that come bundled with a
        // product they own) locked, so they can't re-buy them from the public catalogue.
        if (loggedIn) fetchOwnedProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchPricing() {
        try {
            const res = await api.pricingApi.getPricingList();
            setPricing(await res.json());
        } catch (x: any) {
            api.processErrors(x);
        }
    }

    async function fetchOwnedProducts() {
        try {
            const res = await api.userApi.getProductsList();
            const products = await readJson<{ product: number }[]>(res);
            setOwnedProducts((products ?? []).map((p) => p.product));
        } catch { /* not fatal — the checkout and backend still guard re-purchase */ }
    }

    return (
        <div className="sale">
            <Seo
                title="Plány přípravy a ceník"
                description="Vyber si svůj plán přípravy na přijímačky na medicínu. Online kurzy fyziky, chemie a biologie s flexibilním placením a okamžitým přístupem."
            >
                <script type="application/ld+json">{JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'Course',
                    name: 'Přípravný kurz na přijímačky na medicínu',
                    description: 'Online příprava na přijímací zkoušky na lékařské fakulty – fyzika, chemie a biologie.',
                    inLanguage: 'cs',
                    courseMode: 'online',
                    provider: {
                        '@type': 'EducationalOrganization',
                        name: 'MyMed',
                        url: siteUrl,
                        sameAs: siteUrl,
                    },
                    // Offers mirror the live pricing API; omitted entirely if it's
                    // unavailable so we never publish a fabricated price.
                    ...(pricing.length ? {
                        offers: pricing.map((p) => ({
                            '@type': 'Offer',
                            category: productNames[p.product],
                            price: p.effectivePrice,
                            priceCurrency: 'CZK',
                            availability: PURCHASES_ENABLED
                                ? 'https://schema.org/InStock'
                                : 'https://schema.org/PreOrder',
                            url: `${siteUrl}/nakup`,
                        })),
                    } : {}),
                })}</script>
            </Seo>
            <div className="catalog">
                <header className="catalogHead">
                    <span className="eyebrow">Příprava na přijímačky na medicínu</span>
                    <h1 className="catalogTitle">Vyber si svůj plán přípravy</h1>
                    <p className="catalogSub">
                        Z košíku platíš jen první platbu a hned získáš přístup. U plánů
                        s více platbami ti další platby včas připomeneme e-mailem i přímo v aplikaci.
                    </p>

                    {!PURCHASES_ENABLED && (
                        <div className="launchNote">
                            <span className="dot" />
                            <span>
                                Přihlašování do kurzu spustíme začátkem července. Do té doby
                                si v klidu projdi všechny plány a vyber ten, který Ti sedne.
                            </span>
                        </div>
                    )}
                </header>

                <ProductCatalog pricing={pricing} ownedProducts={ownedProducts} highlightProduct={highlightProduct} />
            </div>
        </div>
    );
}
